import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import licensedPlaceHolder from "../../assets/licensed-care-team/place-holder.png";
import certificationBadge from "../../assets/licensed-care-team/certification-badge.svg";
import locationPin from "../../assets/licensed-care-team/location-pin.svg";
import languageGlobe from "../../assets/licensed-care-team/language-globe.svg";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";
import licensedbyNasp from "../../assets/Licensedby/logo.png";
import licensedbyASA from "../../assets/Licensedby/logo-1.png";
import licensedByASHA from "../../assets/Licensedby/ASHA.png";
import licensedbyABP from "../../assets/Licensedby/logo-3.png";
import licensedbyBacp from "../../assets/Licensedby/logo-4.png";
import {
  dr_Ansley,
  dr_sawsan,
  dr_wael,
  dr_Donia,
  dr_ghezlan,
  dr_aisha,
} from "assets/images";

const headshotMap: Record<string, string> = {
  dr_Ansley: dr_Ansley,
  dr_donia: dr_Donia,
  dr_ghezlan: dr_ghezlan,
  dr_wael: dr_wael,
  dr_aisha: dr_aisha,
  dr_sawsan: dr_sawsan,
};

const logos = {
  nasp: licensedbyNasp,
  apa: licensedbyASA,
  asha: licensedByASHA,
  tabop: licensedbyABP,
  bacb: licensedbyBacp,
};

type LicensedTherapist = {
  name: string;
  role: string;
  certification: string;
  location: string;
  languages: string;
  licensedBy?: keyof typeof logos;
  experience: string;
  headshot: string;
};

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return width;
}

function getCardsPerView(width: number) {
  if (width >= 1440) return 4.5;
  if (width >= 1280) return 4;
  if (width >= 1024) return 3;
  if (width >= 768) return 2;
  if (width >= 350) return 1.5;
  return 1;
}

const MIN_SLIDES_FOR_LOOP = 8;

export function LicensedCareTeam() {
  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  const isRTL = dirAttr === "rtl";

  const viewportWidth = useViewportWidth();
  const cardsPerView = getCardsPerView(viewportWidth);
  const cardWidth = `${(100 / cardsPerView).toFixed(4)}%`;
  const isCompact = cardsPerView <= 1.5;
  const sidePadding = isCompact ? 8 : 24;
  const trackMargin = `0 -${sidePadding}px`;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      slidesToScroll: 1,
      direction: isRTL ? "rtl" : "ltr",
    },
    [Autoplay({ delay: 3500, stopOnInteraction: false })],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ direction: isRTL ? "rtl" : "ltr" });
  }, [isRTL, emblaApi]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const rawMembers =
    (t("licensedCareTeam.members", { returnObjects: true }) as
      | LicensedTherapist[]
      | undefined) ?? [];

  const members: LicensedTherapist[] =
    rawMembers.length > 0 && rawMembers.length < MIN_SLIDES_FOR_LOOP
      ? Array.from(
          { length: Math.ceil(MIN_SLIDES_FOR_LOOP / rawMembers.length) },
          () => rawMembers,
        ).flat()
      : rawMembers;

  const title = t("licensedCareTeam.title");
  const subtitle = t("licensedCareTeam.subtitle");

  return (
    <section
      dir={dirAttr}
      className={`bg-[#ffffff] ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <article className="py-20 px-6 max-w-[1400px] mx-auto">
        <section className="flex flex-col gap-2 max-w-[1175px] justify-center items-center mx-auto">
          <p
            className={`text-center text-2xl md:text-[32px] font-bold text-gray leading-[39px] mt-[18px] ${isArabic ? "font-ibmplex" : "font-funnel"}`}
          >
            {title}
          </p>
          <p className="text-[16px] text-gray text-center max-w-[376px] w-full leading-[130%] mt-2 text-[#2E2E38]">
            {subtitle}
          </p>
        </section>

        <section className="specialist-wrapper mt-10">
          <div
            className="overflow-hidden"
            ref={emblaRef}
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div className="flex" style={{ margin: trackMargin }}>
              {members.map((therapist, index) => (
                <div
                  key={`${therapist.name}-${index}`}
                  className="flex-none min-w-0"
                  style={{
                    width: cardWidth,
                    paddingLeft: `${sidePadding}px`,
                    paddingRight: `${sidePadding}px`,
                  }}
                >
                  <LicensedCareCard
                    {...therapist}
                    headshot={
                      (therapist.headshot
                        ? headshotMap[therapist.headshot]
                        : null) || licensedPlaceHolder
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center items-center gap-2 mt-8">
            {rawMembers.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  index === selectedIndex % rawMembers.length
                    ? "bg-[#000] w-3 h-3"
                    : "bg-gray-300 w-2.5 h-2.5"
                }`}
              />
            ))}
          </div>
        </section>
      </article>
    </section>
  );
}

function LicensedCareCard({
  headshot,
  name,
  role,
  certification,
  location,
  languages,
  experience,
  licensedBy,
}: LicensedTherapist) {
  return (
    <article className="w-full h-full relative max-w-fit">
      {licensedBy ? (
        <div className="absolute top-3 right-2 flex items-center gap-1 bg-white px-[6px] py-2 rounded-[8px] h-[28px] max-w-fit">
          <span className="text-[8px] font-semibold font-inter text-[#2D2D2D]">
            Licensed by
          </span>
          <img
            src={logos[licensedBy]}
            alt="Licensed by logo"
            className="h-[20px] w-auto"
          />
        </div>
      ) : null}
      <img src={headshot} alt={`${name} headshot`} />
      <article className="px-6 pt-4 mt-4">
        <p className="text-[16px] font-semibold">{name}</p>
        <p className="mt-1 text-[#4242CE] text-xs font-medium">{role}</p>
        <article className="my-3">
          <div className="flex gap-2">
            <img
              src={certificationBadge}
              alt="Certification badge icon"
              className="h-3 w-3"
            />
            <p className="text-[#828282] text-xs font-medium">
              {certification}
            </p>
          </div>
          <div className="flex gap-2">
            <img
              src={locationPin}
              alt="Location pin icon"
              className="h-3 w-3"
            />
            <p className="text-[#828282] text-xs font-medium">{location}</p>
          </div>
          <div className="flex gap-2">
            <img
              src={languageGlobe}
              alt="Language globe icon"
              className="h-3 w-3"
            />
            <p className="text-[#828282] text-xs font-medium">{languages}</p>
          </div>
        </article>
        <p className="text-xs font-semibold text-[#2D2D2D]">{experience}</p>
      </article>
    </article>
  );
}
